import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PhoneInput10 from "@/components/site/PhoneInput10";
import PatientAvatar from "@/components/patient/PatientAvatar";
import { toE164, isValid10Digit } from "@/lib/phone";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    MagnifyingGlass, Users, Phone, Envelope, Calendar, UserPlus,
    Storefront, Stethoscope, X, SquaresFour, ListBullets,
    Globe, ShieldWarning, ArrowRight, Plus, Warning, CaretUp, CaretDown,
} from "@phosphor-icons/react";
import { HealthScorePill } from "@/components/admin/HealthScorePill";
import AdminRowActions from "@/components/admin/AdminRowActions";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";

const EMPTY_NEW = { name: "", phone: "", email: "", dob: "", gender: "", notes: "", doctor_id: "" };
const GENDERS = ["", "Female", "Male", "Other", "Prefer not to say"];

const initials = (n) => (n || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

/* Persist the chosen view between sessions. */
const VIEW_KEY = "admin.patientsView";
const readPersistedView = () => {
    if (typeof window === "undefined") return "list";
    return window.localStorage.getItem(VIEW_KEY) === "grid" ? "grid" : "list";
};

export default function AdminPatients() {
    const [items, setItems] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all"); // all | walkin | online | unassigned | disabled | at-risk
    const [view, setView] = useState(readPersistedView);
    const [sortBy, setSortBy] = useState("created"); // created | name | health | lastvisit
    const [sortDir, setSortDir] = useState("desc"); // desc | asc
    /* iter101.20 — Doctor picker in the filter bar. Admin-only affordance
       (equivalent of the doctor portal's "All phases" chooser) so the
       network view can zero-in on one provider's roster with a click. */
    const [doctorFilter, setDoctorFilter] = useState("");
    /* iter101.22 — Clinic filter dropdown. Complements the doctor filter
       for admins running a multi-clinic network. Derived from the loaded
       patient list so we don't add a new API call. */
    const [clinicFilter, setClinicFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [createForm, setCreateForm] = useState(EMPTY_NEW);
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/admin/patients"),
            api.get("/admin/doctors"),
        ]).then(([p, d]) => {
            setItems(p.data);
            setDoctors(d.data.filter((x) => x.is_active !== false));
        }).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* Top-strip stats — recomputed cheaply from the in-memory list. */
    const stats = useMemo(() => {
        const total = items.length;
        let walkin = 0, online = 0, disabled = 0, unassigned = 0, atRisk = 0;
        items.forEach((p) => {
            if (p.is_walk_in) walkin += 1;
            else online += 1;
            if (p.is_active === false) disabled += 1;
            if (!p.managed_by_doctor_id) unassigned += 1;
            if (p.health_tier === "at-risk") atRisk += 1;
        });
        return { total, walkin, online, disabled, unassigned, atRisk };
    }, [items]);

    /* Filter pills then doctor filter then text search, then optional sort. */
    const filtered = useMemo(() => {
        let list = items;
        if (filter === "walkin") list = list.filter((p) => p.is_walk_in);
        else if (filter === "online") list = list.filter((p) => !p.is_walk_in);
        else if (filter === "unassigned") list = list.filter((p) => !p.managed_by_doctor_id);
        else if (filter === "disabled") list = list.filter((p) => p.is_active === false);
        else if (filter === "at-risk") list = list.filter((p) => p.health_tier === "at-risk");

        // iter101.20 — Doctor picker filter. Empty = all doctors, "__unassigned__" = only unassigned patients.
        if (doctorFilter === "__unassigned__") {
            list = list.filter((p) => !p.managed_by_doctor_id);
        } else if (doctorFilter) {
            list = list.filter((p) => p.managed_by_doctor_id === doctorFilter);
        }

        // iter101.22 — Clinic picker filter. Empty = all clinics.
        if (clinicFilter) {
            list = list.filter((p) => (p.managed_by_clinic_name || "") === clinicFilter);
        }

        if (q.trim()) {
            const l = q.trim().toLowerCase();
            list = list.filter((p) =>
                (p.name || "").toLowerCase().includes(l) ||
                (p.phone || "").includes(q) ||
                (p.email || "").toLowerCase().includes(l) ||
                (p.managed_by_doctor_name || "").toLowerCase().includes(l) ||
                (p.managed_by_clinic_name || "").toLowerCase().includes(l)
            );
        }

        if (sortBy === "health") {
            // Patients with no score sink to the bottom regardless of direction.
            const dir = sortDir === "asc" ? 1 : -1;
            list = [...list].sort((a, b) => {
                const aa = a.health_score, bb = b.health_score;
                if (aa == null && bb == null) return 0;
                if (aa == null) return 1;
                if (bb == null) return -1;
                return (aa - bb) * dir;
            });
        } else if (sortBy === "name") {
            list = [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        } else if (sortBy === "lastvisit") {
            list = [...list].sort((a, b) => (b.last_visit_at || "").localeCompare(a.last_visit_at || ""));
        }
        return list;
    }, [items, q, filter, sortBy, sortDir, doctorFilter, clinicFilter]);

    /* iter101.22 — Derived list of unique clinic names for the filter
       dropdown. Deduped + alpha-sorted for a stable UX. */
    const clinicOptions = useMemo(() => {
        const set = new Set();
        items.forEach((p) => {
            const c = (p.managed_by_clinic_name || "").trim();
            if (c) set.add(c);
        });
        return Array.from(set).sort();
    }, [items]);

    const submitCreate = async () => {
        if ((createForm.name || "").trim().length < 2) return toast.error("Enter a valid name");
        if (!isValid10Digit(createForm.phone))
            return toast.error("Enter the patient's 10-digit Indian mobile number");
        if (!createForm.doctor_id) return toast.error("Select a doctor to assign");
        setSaving(true);
        try {
            const { doctor_id, ...payload } = createForm;
            payload.phone = toE164(payload.phone);
            await api.post(`/admin/doctors/${doctor_id}/patients`, payload);
            toast.success(`${createForm.name} added`);
            setCreateForm(EMPTY_NEW);
            setCreating(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setSaving(false);
        }
    };

    /* Refactored 2026-02-26: per-row Edit dialog removed.
       All per-patient management (edit, decommission, cases history,
       appointment history, audit log) now lives at /admin/patients/:id.
       Rows are clickable links. The Add-patient dialog stays here. */

    const setViewPersisted = (v) => {
        try { window.localStorage.setItem(VIEW_KEY, v); } catch { /* private mode */ }
        setView(v);
    };

    return (
        <div data-testid="admin-patients-page" className="space-y-6">
            {/* HEADER — iter101.19 · Aligned to the doctor portal /doctor/patients
                visual grammar so admin and doctor tabs read as one family. */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Work · Patients</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Patients</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        Every patient across the Invisique network. Edit, reassign, or disable any record.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        size="sm"
                        onClick={() => setCreating(true)}
                        className="rounded-full"
                        data-testid="admin-patients-add-btn"
                    >
                        <UserPlus size={14} className="mr-1" weight="bold" /> Add patient
                    </Button>
                </div>
            </div>

            {/* KPI STRIP — clickable tiles matching /doctor/patients grid.
                Every tile drives the matching filter chip so the strip is
                a one-tap drill-in cockpit, not a vanity summary. */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <StatTile
                    label="Total patients" value={stats.total} Icon={Users} tone="primary"
                    hint="Across the network"
                    onClick={() => setFilter("all")}
                    testid="admin-patients-kpi-total"
                />
                <StatTile
                    label="Online bookings" value={stats.online} Icon={Globe} tone="emerald"
                    hint={stats.total > 0 ? `${Math.round(stats.online / stats.total * 100)}% of roster` : "No data yet"}
                    onClick={() => setFilter("online")}
                    testid="admin-patients-kpi-online"
                />
                <StatTile
                    label="Walk-ins" value={stats.walkin} Icon={Storefront} tone="violet"
                    hint="Clinic-added"
                    onClick={() => setFilter("walkin")}
                    testid="admin-patients-kpi-walkin"
                />
                <StatTile
                    label="At-risk" value={stats.atRisk} Icon={Warning} tone="rose"
                    hint={stats.atRisk > 0 ? "Health flagged" : "All clear"}
                    onClick={() => setFilter("at-risk")}
                    testid="admin-patients-kpi-at-risk"
                />
                <StatTile
                    label="Disabled" value={stats.disabled} Icon={ShieldWarning} tone="amber"
                    hint={stats.disabled > 0 ? "Deactivated" : "None"}
                    onClick={() => setFilter("disabled")}
                    testid="admin-patients-kpi-disabled"
                />
            </div>

            {/* FILTER STRIP — row 1: search + sort + doctor filter + view toggle.
                iter101.20 · Matches /doctor/patients filter bar exactly so
                admin and doctor tabs share pixel-identical grammar. Doctor
                filter is the admin equivalent of the doctor's "All phases"
                dropdown — most common admin drill-in is "one provider's roster". */}
            <div className="flex flex-col gap-3">
                <FilterSearchBar
                    query={q}
                    setQuery={setQ}
                    placeholder="Search name, phone, email…"
                    searchTestid="admin-patients-search"
                    testid="admin-patients-filter-bar"
                    rightSlot={(
                        <>
                            <div className="relative">
                                <select
                                    value={sortBy === "created" ? "" : sortBy}
                                    onChange={(e) => setSortBy(e.target.value || "created")}
                                    className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer"
                                    data-testid="admin-patients-sort-select"
                                    aria-label="Sort patients"
                                >
                                    <option value="">Sort</option>
                                    <option value="name">Name A–Z</option>
                                    <option value="lastvisit">Last visit</option>
                                    <option value="health">Health</option>
                                </select>
                                <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                            </div>
                            <div className="relative">
                                <select
                                    value={doctorFilter}
                                    onChange={(e) => setDoctorFilter(e.target.value)}
                                    className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer max-w-[180px] truncate"
                                    data-testid="admin-patients-doctor-filter"
                                    aria-label="Filter by managing doctor"
                                >
                                    <option value="">All doctors</option>
                                    <option value="__unassigned__">Unassigned</option>
                                    {doctors.map((d) => (
                                        <option key={d.id} value={d.id}>{d.name || d.full_name || "Unnamed"}</option>
                                    ))}
                                </select>
                                <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                            </div>
                            <div className="relative">
                                <select
                                    value={clinicFilter}
                                    onChange={(e) => setClinicFilter(e.target.value)}
                                    className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer max-w-[180px] truncate"
                                    data-testid="admin-patients-clinic-filter"
                                    aria-label="Filter by clinic"
                                >
                                    <option value="">All clinics</option>
                                    {clinicOptions.map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                                <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                            </div>
                            <div className="inline-flex items-center rounded-md border border-border bg-card overflow-hidden h-9">
                                <button
                                    type="button" onClick={() => setViewPersisted("list")}
                                    className={`px-2.5 h-full inline-flex items-center justify-center transition ${view === "list" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                                    data-testid="admin-patients-view-list"
                                    aria-pressed={view === "list"}
                                    title="List view"
                                >
                                    <ListBullets size={13} weight="bold" />
                                </button>
                                <button
                                    type="button" onClick={() => setViewPersisted("grid")}
                                    className={`px-2.5 h-full inline-flex items-center justify-center transition ${view === "grid" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                                    data-testid="admin-patients-view-grid"
                                    aria-pressed={view === "grid"}
                                    title="Grid view"
                                >
                                    <SquaresFour size={13} weight="bold" />
                                </button>
                            </div>
                        </>
                    )}
                />
                <FilterChipRow
                    chips={[
                        { id: "all",         label: "All",         count: stats.total,      tone: "primary" },
                        { id: "walkin",      label: "Walk-ins",    count: stats.walkin,     tone: "violet" },
                        { id: "online",      label: "Online",      count: stats.online,     tone: "emerald" },
                        { id: "unassigned",  label: "Unassigned",  count: stats.unassigned, tone: "slate" },
                        { id: "at-risk",     label: "At-risk",     count: stats.atRisk,     tone: "rose" },
                        { id: "disabled",    label: "Disabled",    count: stats.disabled,   tone: "amber" },
                    ]}
                    value={filter}
                    onChange={setFilter}
                    testid="admin-patients-chip-row"
                    chipTestidPrefix="admin-patients-filter"
                />
            </div>

            {/* BODY */}
            {loading ? (
                <div className="text-muted-foreground text-sm text-center py-10">Loading patients…</div>
            ) : filtered.length === 0 ? (
                <EmptyState
                    total={items.length}
                    onAdd={() => setCreating(true)}
                    isFiltered={q.length > 0 || filter !== "all"}
                    onClear={() => { setQ(""); setFilter("all"); }}
                />
            ) : view === "list" ? (
                <ListView rows={filtered} sortBy={sortBy} sortDir={sortDir} onChanged={load} onSort={(by) => {
                    if (sortBy === by) {
                        // toggle dir, or clear when already asc
                        if (sortDir === "desc") setSortDir("asc");
                        else { setSortBy("created"); setSortDir("desc"); }
                    } else { setSortBy(by); setSortDir("desc"); }
                }} />
            ) : (
                <GridView rows={filtered} onChanged={load} />
            )}

            {/* Create dialog */}
            <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
                <DialogContent className="max-w-md" data-testid="admin-patient-create-dialog">
                    <DialogHeader>
                        <DialogTitle>Add a patient</DialogTitle>
                        <DialogDescription>
                            Create a walk-in patient and route them to a specific doctor. The patient will appear in that doctor's portal immediately.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 mt-2">
                        <PatientFormFields form={createForm} setForm={setCreateForm} prefix="admin-create" />
                        <div>
                            <Label className="text-xs font-semibold">Assign to doctor</Label>
                            <select
                                value={createForm.doctor_id}
                                onChange={(e) => setCreateForm({ ...createForm, doctor_id: e.target.value })}
                                className="w-full h-10 mt-1 text-sm rounded-lg border border-border bg-background px-2"
                                data-testid="admin-create-patient-doctor"
                            >
                                <option value="">Select a doctor…</option>
                                {doctors.map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.name} · {d.clinic_name} · {d.city}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <DialogFooter className="mt-3">
                        <Button variant="outline" onClick={() => { setCreating(false); setCreateForm(EMPTY_NEW); }} disabled={saving}>Cancel</Button>
                        <Button onClick={submitCreate} disabled={saving} data-testid="admin-create-patient-save">
                            {saving ? "Saving…" : "Add patient"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit dialog removed 2026-02-26 — content moved to /admin/patients/:id */}
        </div>
    );
}

/* ---------- Hero stat tile (kept local; identical to doctor portal) ---------- */
function StatTile({ label, value, Icon, tone, hint, onClick, testid }) {
    const accent = tone === "emerald" ? "text-emerald-700 bg-emerald-100"
        : tone === "violet" ? "text-violet-700 bg-violet-100"
        : tone === "amber" ? "text-amber-700 bg-amber-100"
        : tone === "rose" ? "text-rose-700 bg-rose-100"
        : "text-primary bg-primary/10";
    const Component = onClick ? "button" : "div";
    return (
        <Component
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`flex items-start gap-3 rounded-xl bg-card border border-border p-3 text-left transition ${onClick ? "hover:border-primary/40 hover:shadow-sm cursor-pointer" : ""}`}
            data-testid={testid}
        >
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
                <div className="text-xl font-display font-bold tabular-nums leading-none mt-1">{value}</div>
                {hint && <div className="text-[10px] text-muted-foreground mt-1 truncate">{hint}</div>}
            </div>
        </Component>
    );
}

/* ---------- LIST VIEW (rows click → /admin/patients/:id) ---------- */
function ListView({ rows, sortBy, sortDir, onSort, onChanged }) {
    const SortableHeader = ({ id, children, className }) => {
        const on = sortBy === id;
        return (
            <button
                type="button"
                onClick={() => onSort(id)}
                className={`inline-flex items-center gap-1 ${on ? "text-foreground" : ""} hover:text-foreground transition`}
                data-testid={`admin-patients-sort-${id}`}
            >
                {children}
                {on && (sortDir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
            </button>
        );
    };
    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="admin-patients-list-view">
            <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                <div className="col-span-3">Patient</div>
                <div className="col-span-2">Phone / Email</div>
                <div className="col-span-3">Managing doctor</div>
                <div className="col-span-2"><SortableHeader id="health">Health</SortableHeader></div>
                <div className="col-span-2 text-right">Action</div>
            </div>
            {rows.map((p) => (
                <Link
                    key={p.id}
                    to={`/admin/patients/${p.id}`}
                    className={`grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-3 md:py-2.5 border-b border-border last:border-0 hover:bg-secondary/30 transition items-center ${p.is_active === false ? "opacity-60" : ""}`}
                    data-testid={`admin-patient-${p.id}`}
                >
                    <div className="md:col-span-3 flex items-center gap-3 min-w-0">
                        <PatientAvatar patient={p} size="md" />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="font-semibold truncate">{p.name || "Unnamed"}</div>
                                {p.is_walk_in && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 uppercase font-bold tracking-wider inline-flex items-center gap-0.5">
                                        <Storefront size={9} weight="fill" /> Walk-in
                                    </span>
                                )}
                                {p.is_active === false && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 uppercase font-bold tracking-wider">
                                        Disabled
                                    </span>
                                )}
                            </div>
                            <div className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                                <Calendar size={9} /> {p.created_at ? new Date(p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                            </div>
                        </div>
                    </div>
                    <div className="hidden md:flex md:col-span-2 flex-col min-w-0 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1.5 truncate"><Phone size={11} /> {p.phone || "—"}</span>
                        {p.email && <span className="flex items-center gap-1.5 truncate mt-0.5"><Envelope size={11} /> {p.email}</span>}
                    </div>
                    <div className="md:col-span-3 flex items-center gap-1.5 text-xs min-w-0">
                        {p.managed_by_doctor_name ? (
                            <>
                                <Stethoscope size={12} className="text-primary shrink-0" />
                                <span className="truncate">
                                    {p.managed_by_doctor_name}
                                    {p.managed_by_clinic_name && <span className="text-muted-foreground"> · {p.managed_by_clinic_name}</span>}
                                </span>
                            </>
                        ) : (
                            <span className="text-amber-700 font-medium">Unassigned</span>
                        )}
                    </div>
                    <div className="md:col-span-2 flex items-center min-w-0">
                        <HealthScorePill score={p.health_score} tier={p.health_tier} testid={`admin-patient-health-${p.id}`} />
                    </div>
                    <div className="md:col-span-2 flex md:justify-end items-center gap-2">
                        <AdminRowActions
                            kind="patient"
                            targetId={p.id}
                            targetUserId={p.id}
                            targetName={p.name || "patient"}
                            isActive={p.is_active !== false}
                            onChanged={onChanged}
                        />
                    </div>
                </Link>
            ))}
        </div>
    );
}

/* ---------- GRID VIEW (rows click → /admin/patients/:id) ---------- */
function GridView({ rows, onChanged }) {
    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-patients-grid-view">
            {rows.map((p) => (
                <Link
                    key={p.id}
                    to={`/admin/patients/${p.id}`}
                    className={`block bg-card border border-border rounded-xl p-4 hover:border-primary/40 hover:shadow-sm transition ${p.is_active === false ? "opacity-60" : ""}`}
                    data-testid={`admin-patient-${p.id}`}
                >
                    <div className="flex items-start gap-3">
                        <PatientAvatar patient={p} size="lg" />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="font-display font-semibold truncate">{p.name || "Unnamed"}</div>
                                {p.is_walk_in && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 uppercase font-bold tracking-wider inline-flex items-center gap-0.5">
                                        <Storefront size={9} weight="fill" /> Walk-in
                                    </span>
                                )}
                                {p.is_active === false && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 uppercase font-bold tracking-wider">
                                        Disabled
                                    </span>
                                )}
                            </div>
                            <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1 mt-0.5">
                                <Phone size={10} /> {p.phone || "—"}
                            </div>
                            {p.managed_by_doctor_name && (
                                <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                                    <Stethoscope size={10} className="text-primary" /> {p.managed_by_doctor_name}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-2">
                        <HealthScorePill score={p.health_score} tier={p.health_tier} testid={`admin-patient-health-grid-${p.id}`} />
                        <div className="flex items-center gap-2">
                            <AdminRowActions
                                kind="patient"
                                targetId={p.id}
                                targetUserId={p.id}
                                targetName={p.name || "patient"}
                                isActive={p.is_active !== false}
                                onChanged={onChanged}
                                dataTestidPrefix="admin-patient-grid"
                            />
                            <span className="text-[11px] font-semibold text-primary inline-flex items-center gap-1">
                                View profile <ArrowRight size={11} />
                            </span>
                        </div>
                    </div>
                </Link>
            ))}
        </div>
    );
}

/* iter101.46 — Legacy ImpersonatePatientBtn removed. Both list + grid
   views now use the unified `<AdminRowActions kind="patient" />` cluster
   that renders Enable/Disable + Impersonate consistently across every
   admin surface (Team & Access, Doctors, Patients). */

/* ---------- Empty state ---------- */
function EmptyState({ total, onAdd, isFiltered, onClear }) {
    if (isFiltered && total > 0) {
        return (
            <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center" data-testid="admin-patients-empty">
                <MagnifyingGlass size={28} className="mx-auto text-muted-foreground mb-3" />
                <div className="font-display text-lg font-bold">No patients match your filters</div>
                <p className="text-xs text-muted-foreground mt-1.5">Try clearing your search or switching to a different filter.</p>
                <Button onClick={onClear} variant="outline" className="rounded-full mt-4" data-testid="admin-patients-empty-clear">
                    Clear filters
                </Button>
            </div>
        );
    }
    return (
        <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center" data-testid="admin-patients-empty">
            <UserPlus size={28} className="mx-auto text-muted-foreground mb-3" />
            <div className="font-display text-lg font-bold">No patients yet</div>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">
                Add walk-in patients here so you can route them to a specific doctor. Online bookings show up automatically.
            </p>
            <Button onClick={onAdd} className="rounded-full mt-5" data-testid="admin-patients-empty-add">
                <Plus size={14} className="mr-1.5" weight="bold" /> Add your first patient
            </Button>
        </div>
    );
}

function PatientFormFields({ form, setForm, prefix, hidePhone }) {
    return (
        <>
            <div>
                <Label className="text-xs font-semibold">Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 mt-1" data-testid={`${prefix}-patient-name`} />
            </div>
            {!hidePhone && (
                <div>
                    <Label className="text-xs font-semibold">Phone (10-digit Indian mobile)</Label>
                    <div className="h-10 mt-1">
                        <PhoneInput10
                            value={form.phone}
                            onChange={(v) => setForm({ ...form, phone: v })}
                            inputClassName="h-10"
                            testid={`${prefix}-patient-phone`}
                        />
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">If this phone is already in use, we'll link the existing patient instead of creating a duplicate.</p>
                </div>
            )}
            <div>
                <Label className="text-xs font-semibold">Email (optional)</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10 mt-1" data-testid={`${prefix}-patient-email`} />
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <Label className="text-xs font-semibold">DOB</Label>
                    <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="h-10 mt-1" />
                </div>
                <div>
                    <Label className="text-xs font-semibold">Gender</Label>
                    <select
                        value={form.gender}
                        onChange={(e) => setForm({ ...form, gender: e.target.value })}
                        className="w-full h-10 mt-1 text-sm rounded-lg border border-border bg-background px-2"
                    >
                        {GENDERS.map((g) => <option key={g} value={g}>{g || "Select…"}</option>)}
                    </select>
                </div>
            </div>
            <div>
                <Label className="text-xs font-semibold">Notes (optional)</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" />
            </div>
        </>
    );
}
